import { ArrowBack, ArrowForward, Close } from "@mui/icons-material";
import * as MUI from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import React, { Fragment, useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaCheckCircle } from "react-icons/fa";
import { FaFileCircleCheck } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import { cancelButtonVariant } from "../../../../components/buttons/ConfirmAndCancelButtons";
import { fileControlField } from "../../../../components/form/FileUploadBody";
import { GlobalDataContext } from "../../../../contexts/GlobalData";
import { DOWNLOAD_DR_LETTER, TREATMENT } from "../../../../enums/ApiPaths";
import {
  ADD_DOCUMENTS,
  VIEW_WAREHOUSE,
} from "../../../../enums/PermissionTitles";
import { TemplateCategory } from "../../../../enums/TemplateCategory";
import useFileDownload from "../../../../hooks/useFileDownload";
import { getPrepredFiles, useIsMobile } from "../../../../utils/Functions";
import ActionRow from "../../../admin/clinic/components/actions/ActionRow";
import AddVaccinationForm from "../../../vaccination/components/AddVactinationForm";
import useFinishAppointment from "../hooks/useFinishAppointment";
import Biometrics from "./Biometrics";
import DoctorsLetter from "./DoctorsLetter";
import FileUploadStep from "./FileUploadStep";
import OverView, { prepareResourcesForPayload } from "./OverView";
import ResourcesManager from "./ResourcesManager";
import { StepIcon } from "./utils/FinishMeetingUtils";
import FactCheckIcon from "@mui/icons-material/FactCheck";

const FinishMeetingButton = ({ finishMeetingCondition }) => {
  const [activeStep, setActiveStep] = useState(0);
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const canUploadFiles = isPermissionAllowed(ADD_DOCUMENTS);
  const canViewWarehouse = isPermissionAllowed(VIEW_WAREHOUSE);

  const queryClient = useQueryClient();
  const methods = useForm();
  const isMobile = useIsMobile();
  const { id } = useParams();
  const { t } = useTranslation();
  const { downloadDoctorsLetter } = useFileDownload(DOWNLOAD_DR_LETTER, id);
  const url = TREATMENT + id;
  const submittion = useFinishAppointment(url, id);
  const controlledFiles = methods.watch(fileControlField) ?? [];

  const selectedItems = methods.watch("selectedItems") ?? new Map();
  const resources = Array.from(selectedItems.values());

  const steps = React.useMemo(
    () => buildSteps({ canUploadFiles, canViewWarehouse }),
    [canUploadFiles, canViewWarehouse],
  );

  const handleOpenDialog = () => {
    submittion.setIsOpen(true);
  };

  const handleCloseDialog = () => {
    submittion.setIsOpen(false);
    setActiveStep(0);
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  async function handleFinishMeeting() {
    const { biometrics, vaccinations, description, treatment } =
      methods.getValues();
    const files = await getPrepredFiles(controlledFiles);
    delvals.forEach((item) => delete treatment?.[item]); //Removing single vaccine fields

    const formattedVaccinations = (vaccinations || []).map((v) => ({
      ...v,
      dateOfApplication: dayjs.isDayjs(v.dateOfApplication)
        ? v.dateOfApplication.format("DD/MM/YYYY")
        : v.dateOfApplication,
      expirationDate: dayjs.isDayjs(v.expirationDate)
        ? v.expirationDate.format("DD/MM/YYYY")
        : v.expirationDate,
      immunityFromDate: dayjs.isDayjs(v.immunityFromDate)
        ? v.immunityFromDate.format("DD/MM/YYYY")
        : v.immunityFromDate,
      immunityToDate: dayjs.isDayjs(v.immunityToDate)
        ? v.immunityToDate.format("DD/MM/YYYY")
        : v.immunityToDate,
      powderExpirationDate: dayjs.isDayjs(v.powderExpirationDate)
        ? v.powderExpirationDate.format("DD/MM/YYYY")
        : v.powderExpirationDate,
      solventExpirationDate: dayjs.isDayjs(v.solventExpirationDate)
        ? v.solventExpirationDate.format("DD/MM/YYYY")
        : v.solventExpirationDate,
    }));

    const mappedResources = prepareResourcesForPayload(resources);

    const submitData = {
      treatment: {
        vaccinations: formattedVaccinations,
        description: description || null,
      },
      biometrics: biometrics || null,
      files: files || [],
      resources: mappedResources,
    };

    const res = await submittion.submitCallback(submitData);

    if (res) {
      localStorage.removeItem("biometricState");
    }
  }

  async function handleFinishAndDownloadMeeting() {
    await handleFinishMeeting();
    await downloadDoctorsLetter();
  }

  if (submittion.loading) {
    return <MUI.CircularProgress size="30px" />;
  }

  return (
    <Fragment>
      {finishMeetingCondition && (
        <ActionRow
          justButton
          icon={<FactCheckIcon />}
          label={t("FinishMeeting")}
          description={t("FinishMeetingDesc")}
          onClick={() => handleOpenDialog()}
          testId={"appointment.details.finish-button"}
        />
      )}

      <MUI.Dialog
        open={submittion.getIsOpen()}
        maxWidth="lg"
        fullScreen={isMobile}
        fullWidth
        data-testid="appointment.finish-meeting.dialog"
      >
        <FormProvider {...methods}>
          <MUI.DialogContent
            sx={{
              minHeight: 750,
              px: isMobile ? 0.25 : 3,
            }}
          >
            {isMobile ? (
              <MUI.Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <MUI.MobileStepper
                  variant="dots"
                  steps={steps.length}
                  position="static"
                  activeStep={activeStep}
                  sx={{
                    mb: 2,
                    flexGrow: 1,
                    justifyContent: "center",
                    padding: 0,
                    backgroundColor: "transparent",
                    //Makes stepper dots bigger with more space in-between
                    "& .MuiMobileStepper-dot": {
                      transform: "scale(1.5)",
                      margin: "0 6px",
                    },
                  }}
                />
                <MUI.Typography variant="h6" textAlign="center">
                  {t(steps[activeStep].title)}
                </MUI.Typography>
              </MUI.Box>
            ) : (
              <MUI.Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, index) => (
                  <MUI.Step key={index}>
                    <MUI.StepLabel StepIconComponent={StepIcon}>
                      {t(label.title)}
                    </MUI.StepLabel>
                  </MUI.Step>
                ))}
              </MUI.Stepper>
            )}

            <MUI.Container sx={{ mt: 5 }}>
              {steps[activeStep].body}
            </MUI.Container>
          </MUI.DialogContent>
        </FormProvider>

        <MUI.DialogActions sx={{ px: 4, pb: 3, mt: 2 }}>
          <MUI.Stack spacing={isMobile ? 4 : 2} width="100%">
            <MUI.Stack direction="row" alignItems="center" width="100%">
              {isMobile ? (
                <MUI.IconButton
                  color="error"
                  onClick={handleCloseDialog}
                  data-testid="appointment.finish-meeting.close-button"
                >
                  <Close />
                </MUI.IconButton>
              ) : (
                <MUI.Button
                  color="error"
                  variant={cancelButtonVariant}
                  onClick={handleCloseDialog}
                  data-testid="appointment.finish-meeting.close-button"
                >
                  {t("Close")}
                </MUI.Button>
              )}

              <MUI.Box sx={{ flexGrow: 1 }} />

              {isMobile ? (
                <MUI.IconButton
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  data-testid="appointment.finish-meeting.back-button"
                >
                  <ArrowBack />
                </MUI.IconButton>
              ) : (
                <MUI.Button
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  variant="contained"
                  data-testid="appointment.finish-meeting.back-button"
                >
                  {t("Back")}
                </MUI.Button>
              )}

              {isMobile ? (
                <MUI.IconButton
                  onClick={handleNext}
                  disabled={activeStep === steps.length - 1}
                  data-testid="appointment.finish-meeting.next-button"
                >
                  <ArrowForward />
                </MUI.IconButton>
              ) : (
                <MUI.Button
                  onClick={handleNext}
                  variant="contained"
                  disabled={activeStep === steps.length - 1}
                  sx={{ ml: 1 }}
                  data-testid="appointment.finish-meeting.next-button"
                >
                  {t("Next")}
                </MUI.Button>
              )}

              {!isMobile && (
                <>
                  <MUI.Button
                    title={t("AppointmentFinishTitle")}
                    disabled={activeStep !== steps?.length - 1}
                    onClick={handleFinishMeeting}
                    color="primary"
                    variant="contained"
                    startIcon={<FaCheckCircle />}
                    sx={{ ml: 1 }}
                    data-testid="appointment.finish-meeting.finish-button"
                  >
                    {t("Finish")}
                  </MUI.Button>

                  <MUI.Button
                    title={t("AppointmentFinishAndDownloadTitle")}
                    disabled={activeStep !== steps?.length - 1}
                    onClick={handleFinishAndDownloadMeeting}
                    color="primary"
                    variant="outlined"
                    startIcon={<FaFileCircleCheck />}
                    sx={{ whiteSpace: "nowrap", ml: 1 }}
                    data-testid="appointment.finish-meeting.finish-and-download-button"
                  >
                    {t("FinishAndDownload")}
                  </MUI.Button>
                </>
              )}
            </MUI.Stack>

            {isMobile && (
              <MUI.Stack direction="column" spacing={2} width="100%">
                <MUI.Button
                  title={t("AppointmentFinishTitle")}
                  disabled={activeStep !== steps?.length - 1}
                  onClick={handleFinishMeeting}
                  color="primary"
                  variant="contained"
                  startIcon={<FaCheckCircle />}
                  fullWidth
                  data-testid="appointment.finish-meeting.finish-button"
                >
                  {t("Finish")}
                </MUI.Button>

                <MUI.Button
                  title={t("AppointmentFinishAndDownloadTitle")}
                  disabled={activeStep !== steps?.length - 1}
                  onClick={handleFinishAndDownloadMeeting}
                  color="primary"
                  variant="outlined"
                  startIcon={<FaFileCircleCheck />}
                  sx={{ whiteSpace: "nowrap" }}
                  fullWidth
                  data-testid="appointment.finish-meeting.finish-and-download-button"
                >
                  {t("FinishAndDownload")}
                </MUI.Button>
              </MUI.Stack>
            )}
          </MUI.Stack>
        </MUI.DialogActions>
      </MUI.Dialog>
      <MUI.Divider />
    </Fragment>
  );
};

export default FinishMeetingButton;

const ALL_STEPS = [
  {
    key: "DoctorsLetter",
    title: "DoctorsLetter",
    order: 10,
    isVisible: () => true,
    body: <DoctorsLetter category={TemplateCategory.APPOINTMENT} />,
  },
  {
    key: "Biometrics",
    title: "Biometrics",
    order: 20,
    isVisible: () => true,
    body: <Biometrics />,
  },
  {
    key: "Vaccines",
    title: "Vaccines",
    order: 30,
    isVisible: () => true,
    body: <AddVaccinationForm />,
  },
  {
    key: "FileUpload",
    title: "FileUpload",
    order: 40,
    isVisible: ({ canUploadFiles }) => canUploadFiles,
    body: <FileUploadStep />,
  },
  {
    key: "Resources",
    title: "Resources",
    order: 50,
    isVisible: ({ canViewWarehouse }) => canViewWarehouse,
    body: <ResourcesManager />,
  },
  {
    key: "Overview",
    title: "Overview",
    order: 60,
    isVisible: () => true,
    body: <OverView />,
  },
];

function buildSteps(permissions) {
  return ALL_STEPS.filter((step) => step.isVisible(permissions)).sort(
    (a, b) => a.order - b.order,
  );
}

const delvals = [
  "dateOfApplication",
  "expirationDate",
  "manufacturer",
  "name",
  "serialNumber",
  "heartRate",
  "height",
  "length",
  "temperature",
  "weight",
  "breathingFrequency",
];
